import { ComponentProps, ElementType } from 'react';
import styled from 'styled-components';
import BaseTab, { TabProps } from '@mui/material/Tab';
import { componentToken, declaration, fontWeight } from '../../style/utils';

// The hover underline matches the 2px selected indicator of `Tabs`.
const HOVER_UNDERLINE_WIDTH = '2px';

type StyledTabProps<C extends ElementType = ElementType> = TabProps & {
  $size?: 'small';
  component?: C;
} & ComponentProps<C>;

const Tab = styled(BaseTab)<StyledTabProps>`
  text-transform: none;
  color: ${({ theme }) => theme.palette.content.primary};
  font-size: ${componentToken('tabs.fontSize')};
  font-weight: ${fontWeight('regular')};
  &.MuiTab-root {
    height: ${({ $size }) => ($size === 'small' ? '42px' : undefined)};
    ${({ theme }) =>
      declaration('min-width', theme.tokens.components.tabs.minWidth)}
  }

  ${({ theme }) => {
    const {
      hoverBackground,
      hoverRadius,
      hoverUnderline,
      hoverGap,
      selectedHoverBackground,
    } = theme.tokens.components.tabs;
    if (!hoverUnderline) {
      return `:hover {
        background-color: ${hoverBackground};
        ${declaration('border-radius', hoverRadius)}
      }`;
    }
    // The hover is a rounded background with a separate underline at the
    // bottom, in line with the selected indicator, and a gap between them.
    // The selected tab's background takes the selected color instead, with no
    // underline, and its indicator widens (see `Tabs`). Vertical tabs mark
    // selection on the left, so they keep a plain hover.
    return `
      isolation: isolate;
      &::before, &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        opacity: 0;
        pointer-events: none;
      }
      &::before {
        top: 0;
        bottom: calc(${hoverGap} + ${HOVER_UNDERLINE_WIDTH});
        z-index: -1;
        border-radius: ${hoverRadius};
        background: ${hoverBackground};
      }
      &::after {
        bottom: 0;
        height: ${HOVER_UNDERLINE_WIDTH};
        border-radius: ${HOVER_UNDERLINE_WIDTH};
        background: ${hoverUnderline};
      }
      &:not(.Mui-selected):hover::before, &:not(.Mui-selected):hover::after {
        opacity: 1;
      }
      &.Mui-selected:hover::before {
        opacity: 1;
        background: ${selectedHoverBackground};
      }
      .MuiTabs-vertical &::before, .MuiTabs-vertical &::after { display: none; }
      .MuiTabs-vertical &:hover { background-color: ${hoverBackground}; }
    `;
  }}

  & > svg {
    color: ${({ theme }) => theme.palette.content.secondary};
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.content.translucent.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};

    & > svg {
      color: ${({ theme }) => theme.palette.content.translucent.primary};
    }
  }
`;

export default Tab;
